import { Search } from "lucide-react";
import { useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { Modal } from "@/shared/ui/modal";

import { useResolveSkus } from "../api/use-resolve-skus";
import { useSearchSkus, type SkuListItem } from "../api/use-search-skus";

import { PrimaryButton, SecondaryButton } from "./buttons";

type ResolvedSku = components["schemas"]["ResolvedSku"];

/** ~250ms so typing doesn't fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 250;

type PickerTab = "search" | "paste";

export interface SkuPickerModalProps {
  open: boolean;
  /** `single` is used for the gift add-on picker; `multi` for trigger/constraint/exclude. */
  mode: "single" | "multi";
  onClose: () => void;
  /** Returns the chosen resolved SKUs (caller maps to RuleProduct rows). */
  onConfirm: (skus: ResolvedSku[]) => void;
  /**
   * SKU codes already on the rule for this picker's kind — rendered as
   * checked so re-searching an already-added SKU doesn't look unselected.
   * Not merged into `selected`: confirm() still only emits newly-picked
   * rows; the caller already dedupes on add, so re-checking one of these
   * is a harmless no-op.
   */
  alreadySelected?: string[];
}

/**
 * Reusable SKU picker (spec §5.4). Two surfaces feeding one selection:
 *
 *   • Search (default) — live, debounced browse of the SKU master catalogue via
 *     `GET /v1/sku?name_contains=` (cross-domain read; see `useSearchSkus`).
 *   • Paste — bulk-paste codes (≤500) → resolve via `POST /v1/skus/resolve` →
 *     check. Kept intact for power users who paste long lists.
 *
 * Both tabs select into a shared `selected` map keyed by sku, so confirm returns
 * the same `ResolvedSku[]` to the caller regardless of how the rows were picked.
 * `single` mode keeps exactly one selection; `multi` accumulates.
 */
export function SkuPickerModal({
  open,
  mode,
  onClose,
  onConfirm,
  alreadySelected = [],
}: SkuPickerModalProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const resolve = useResolveSkus();

  const [tab, setTab] = useState<PickerTab>("search");

  // Search state.
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");

  // Paste state.
  const [paste, setPaste] = useState("");
  const [resolved, setResolved] = useState<ResolvedSku[]>([]);
  const [unrecognized, setUnrecognized] = useState<string[]>([]);

  // Shared selection: a map keyed by sku so both surfaces (and confirm) agree.
  const [selected, setSelected] = useState<Map<string, ResolvedSku>>(new Map());

  // Debounce the term that drives the search query.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term]);

  const search = useSearchSkus(debounced, open && tab === "search");
  const searchResults = search.data ?? [];

  function pick(row: ResolvedSku): void {
    setSelected((prev) => {
      if (mode === "single") {
        // Single mode: replace the selection with just this row.
        return new Map([[row.sku, row]]);
      }
      const next = new Map(prev);
      if (next.has(row.sku)) next.delete(row.sku);
      else next.set(row.sku, row);
      return next;
    });
  }

  function runResolve(): void {
    const codes = paste
      .split(/[\n,]/)
      .map((c) => c.trim())
      .filter(Boolean)
      .slice(0, 500);
    if (codes.length === 0) return;
    resolve.mutate(codes, {
      onSuccess: (data) => {
        setResolved(data.resolved);
        setUnrecognized(data.unrecognized);
        // Pre-check everything in multi; single waits for an explicit pick.
        setSelected(mode === "multi" ? new Map(data.resolved.map((r) => [r.sku, r])) : new Map());
      },
    });
  }

  function confirm(): void {
    onConfirm([...selected.values()]);
    reset();
    onClose();
  }

  function reset(): void {
    setTab("search");
    setTerm("");
    setDebounced("");
    setPaste("");
    setResolved([]);
    setUnrecognized([]);
    setSelected(new Map());
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  const inputType = mode === "single" ? "radio" : "checkbox";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("picker.title")}
      subtitle={mode === "single" ? t("picker.subtitle_single") : t("picker.subtitle_multi")}
      width={520}
      footer={
        <>
          <SecondaryButton type="button" onClick={handleClose}>
            {t("picker.cancel")}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            data-testid="sku-picker-confirm"
            disabled={selected.size === 0}
            onClick={confirm}
          >
            {t("picker.add", { count: selected.size })}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-3">
        {/* Tab toggle: Search | Paste. */}
        <div
          role="tablist"
          aria-label={t("picker.title")}
          className="inline-flex rounded-[var(--control-r)] border border-[var(--border-subtle)] bg-[var(--canvas)] p-0.5"
        >
          {(["search", "paste"] as const).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              data-testid={`sku-picker-tab-${key}`}
              onClick={() => setTab(key)}
              className={[
                "rounded-[calc(var(--control-r)-2px)] px-3 py-1.5 text-[13px] font-semibold transition",
                tab === key
                  ? "bg-white text-[var(--text-primary)] shadow-[var(--sh-pop)]"
                  : "text-[var(--text-subtle)] hover:text-[var(--text-secondary)]",
              ].join(" ")}
            >
              {t(key === "search" ? "picker.tab_search" : "picker.tab_paste")}
            </button>
          ))}
        </div>

        {tab === "search" ? (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-[var(--text-secondary)]">
              {t("picker.search_label")}
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
                aria-hidden
              />
              <input
                type="text"
                data-testid="sku-picker-search-input"
                value={term}
                placeholder={t("picker.search_placeholder")}
                onChange={(e) => setTerm(e.target.value)}
                className="h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-[38px] pr-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring"
              />
            </div>

            {search.isFetching && searchResults.length === 0 ? (
              <p
                className="px-1 py-2 text-[13px] text-[var(--text-subtle)]"
                data-testid="sku-picker-search-loading"
              >
                {t("picker.search_loading")}
              </p>
            ) : searchResults.length === 0 ? (
              <p
                className="px-1 py-2 text-[13px] text-[var(--text-subtle)]"
                data-testid="sku-picker-search-empty"
              >
                {debounced.trim() ? t("picker.search_no_results") : t("picker.search_empty")}
              </p>
            ) : (
              <ul className="max-h-64 overflow-auto rounded-[var(--control-r)] border border-[var(--border-subtle)]">
                {searchResults.map((row) => (
                  <SkuRow
                    key={row.sku}
                    row={row}
                    inputType={inputType}
                    checked={selected.has(row.sku) || alreadySelected.includes(row.sku)}
                    onToggle={() => pick(row)}
                    testId={`sku-picker-result-${row.sku}`}
                  />
                ))}
              </ul>
            )}

            {selected.size > 0 && (
              <p
                className="text-[13px] text-[var(--text-subtle)]"
                data-testid="sku-picker-selected"
              >
                {t("picker.selected_count", { count: selected.size })}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-[var(--text-secondary)]">
              {t("builder.sku_paste.label")}
            </label>
            <textarea
              data-testid="sku-picker-paste"
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              rows={4}
              className="w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white p-2.5 text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
            />
            <div className="flex items-center gap-3">
              <SecondaryButton
                type="button"
                data-testid="sku-picker-resolve"
                disabled={resolve.isPending || paste.trim().length === 0}
                onClick={runResolve}
              >
                {t("builder.sku_paste.resolve")}
              </SecondaryButton>
              {(resolved.length > 0 || unrecognized.length > 0) && (
                <span
                  className="text-[13px] text-[var(--text-subtle)]"
                  data-testid="sku-picker-result"
                >
                  {t("builder.sku_paste.result", {
                    found: resolved.length,
                    unrecognized: unrecognized.length,
                  })}
                </span>
              )}
            </div>

            {resolved.length > 0 && (
              <ul className="max-h-64 overflow-auto rounded-[var(--control-r)] border border-[var(--border-subtle)]">
                {resolved.map((r) => (
                  <SkuRow
                    key={r.sku}
                    row={r}
                    inputType={inputType}
                    checked={selected.has(r.sku) || alreadySelected.includes(r.sku)}
                    onToggle={() => pick(r)}
                    testId="sku-picker-row-check"
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

interface SkuRowProps {
  row: ResolvedSku | SkuListItem;
  inputType: "radio" | "checkbox";
  checked: boolean;
  onToggle: () => void;
  testId: string;
}

/** One selectable SKU row, shared by the search and paste lists. */
function SkuRow({ row, inputType, checked, onToggle, testId }: SkuRowProps): JSX.Element {
  return (
    <li className="flex items-center gap-3 border-b border-[var(--border-subtle)] px-3 py-2 last:border-0">
      <input
        type={inputType}
        name="sku-pick"
        data-testid={testId}
        aria-label={row.sku}
        checked={checked}
        onChange={onToggle}
      />
      <span className="font-mono text-[13px] font-semibold text-[var(--text-primary)]">
        {row.sku}
      </span>
      <span className="truncate text-[13px] text-[var(--text-secondary)]">{row.name}</span>
    </li>
  );
}
